import { useRef, useState, type ChangeEvent } from 'react'
import {
  Truck,
  ShieldCheck,
  Headphones,
  Percent,
  Tag,
  Gift,
  CreditCard,
  Lock,
  PackageCheck,
  Package,
  Wrench,
  Star,
  Heart,
  Clock,
  Phone,
  MessageCircle,
  Mail,
  MapPin,
  Award,
  BadgeCheck,
  Zap,
  ThumbsUp,
  Banknote,
  RefreshCw,
  Sparkles,
  CheckCircle2,
  Settings,
  Car,
  Battery,
  Cog,
  Loader2,
  Upload,
  Link as LinkIcon,
  Image as ImageIcon,
  RotateCcw,
  type LucideIcon,
} from 'lucide-react'

interface Props {
  value: string
  onChange: (v: string) => void
}

/**
 * Catálogo curado de iconos Lucide para los iconos del strip del footer.
 * IMPORTANTE: mantener este array EXACTAMENTE en el mismo orden y con los
 * mismos nombres que la constante AVAILABLE_LUCIDE del helper backend
 * (apps/web/lib/icon-render.ts) para que la UI ofrezca solo iconos que el
 * backend sabe renderizar.
 */
const AVAILABLE_LUCIDE: { name: string; Comp: LucideIcon }[] = [
  { name: 'Truck', Comp: Truck },
  { name: 'ShieldCheck', Comp: ShieldCheck },
  { name: 'Headphones', Comp: Headphones },
  { name: 'Percent', Comp: Percent },
  { name: 'Tag', Comp: Tag },
  { name: 'Gift', Comp: Gift },
  { name: 'CreditCard', Comp: CreditCard },
  { name: 'Lock', Comp: Lock },
  { name: 'PackageCheck', Comp: PackageCheck },
  { name: 'Package', Comp: Package },
  { name: 'Wrench', Comp: Wrench },
  { name: 'Star', Comp: Star },
  { name: 'Heart', Comp: Heart },
  { name: 'Clock', Comp: Clock },
  { name: 'Phone', Comp: Phone },
  { name: 'MessageCircle', Comp: MessageCircle },
  { name: 'Mail', Comp: Mail },
  { name: 'MapPin', Comp: MapPin },
  { name: 'Award', Comp: Award },
  { name: 'BadgeCheck', Comp: BadgeCheck },
  { name: 'Zap', Comp: Zap },
  { name: 'ThumbsUp', Comp: ThumbsUp },
  { name: 'Banknote', Comp: Banknote },
  { name: 'RefreshCw', Comp: RefreshCw },
  { name: 'Sparkles', Comp: Sparkles },
  { name: 'CheckCircle2', Comp: CheckCircle2 },
  { name: 'Settings', Comp: Settings },
  { name: 'Car', Comp: Car },
  { name: 'Battery', Comp: Battery },
  { name: 'Cog', Comp: Cog },
]

const LUCIDE_BY_NAME: Record<string, LucideIcon> = AVAILABLE_LUCIDE.reduce(
  (acc, { name, Comp }) => {
    acc[name] = Comp
    return acc
  },
  {} as Record<string, LucideIcon>,
)

type Mode = 'lucide' | 'upload' | 'url'

function detectMode(value: string): Mode {
  if (value.startsWith('lucide:')) return 'lucide'
  if (value.startsWith('http://') || value.startsWith('https://')) return 'url'
  if (value.startsWith('/')) return 'upload' // rutas servidas por el sitio (/uploads/library/…)
  return 'lucide'
}

/**
 * Selector de icono con 3 modos:
 *   a) Icono Lucide  →  value="lucide:Truck"
 *   b) Subir imagen  →  value="/uploads/library/xxx.png"
 *   c) URL externa   →  value="https://…"
 *
 * Si value === '' el footer cae al SVG hardcoded default (no rompe nada).
 */
export function IconPicker({ value, onChange }: Props) {
  const [mode, setMode] = useState<Mode>(() => detectMode(value))
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  function switchMode(m: Mode) {
    setMode(m)
    setUploadError(null)
  }

  function clearAll() {
    onChange('')
    setUploadError(null)
    setMode('lucide')
  }

  async function onFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploadError(null)
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const r = await fetch('/api/admin/image-library', {
        method: 'POST',
        credentials: 'same-origin',
        body: fd,
      })
      const data = (await r.json().catch(() => ({}))) as {
        ok?: boolean
        data?: { url?: string }
        error?: string
      }
      if (!r.ok || !data.ok || !data.data?.url) {
        throw new Error(data.error || `HTTP ${r.status}`)
      }
      onChange(data.data.url)
    } catch (err) {
      setUploadError((err as Error).message)
    } finally {
      setUploading(false)
    }
  }

  const lucideName = value.startsWith('lucide:') ? value.slice('lucide:'.length) : ''
  const LucidePreview = lucideName ? LUCIDE_BY_NAME[lucideName] : null

  return (
    <div className="rounded-xl border border-ink-200 bg-ink-50/40 p-3">
      {/* Preview + acciones */}
      <div className="mb-3 flex items-center gap-3">
        <div className="grid size-14 shrink-0 place-items-center rounded-lg border border-ink-200 bg-white">
          {value === '' ? (
            <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">
              Default
            </span>
          ) : LucidePreview ? (
            <LucidePreview className="size-7 text-brand-600" strokeWidth={1.5} />
          ) : value ? (
            <img
              src={value}
              alt="preview"
              className="size-10 object-contain"
              onError={(e) => {
                ;(e.currentTarget as HTMLImageElement).style.opacity = '0.3'
              }}
            />
          ) : null}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs text-ink-600">
            {value === '' ? (
              <span className="italic text-ink-500">Usa el icono SVG por defecto</span>
            ) : (
              <code className="font-mono text-[11px]">{value}</code>
            )}
          </div>
          <button
            type="button"
            onClick={clearAll}
            className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-ink-500 hover:text-brand-600"
          >
            <RotateCcw className="size-3" /> Restaurar default
          </button>
        </div>
      </div>

      {/* Tabs de modo */}
      <div className="mb-3 flex rounded-lg border border-ink-200 bg-white p-0.5">
        <ModeTab active={mode === 'lucide'} onClick={() => switchMode('lucide')} icon={<Sparkles className="size-3" />}>
          Lucide
        </ModeTab>
        <ModeTab active={mode === 'upload'} onClick={() => switchMode('upload')} icon={<Upload className="size-3" />}>
          Subir
        </ModeTab>
        <ModeTab active={mode === 'url'} onClick={() => switchMode('url')} icon={<LinkIcon className="size-3" />}>
          URL
        </ModeTab>
      </div>

      {/* Contenido por modo */}
      {mode === 'lucide' && (
        <div>
          <select
            value={lucideName}
            onChange={(e) => {
              const n = e.target.value
              onChange(n ? `lucide:${n}` : '')
            }}
            className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
          >
            <option value="">— Elegir icono —</option>
            {AVAILABLE_LUCIDE.map(({ name }) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          {/* Grid visual de los iconos disponibles */}
          <div className="mt-3 grid grid-cols-6 gap-1.5 sm:grid-cols-8">
            {AVAILABLE_LUCIDE.map(({ name, Comp }) => {
              const sel = name === lucideName
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => onChange(`lucide:${name}`)}
                  title={name}
                  className={`grid aspect-square place-items-center rounded-md border transition-colors ${
                    sel
                      ? 'border-brand-500 bg-brand-50 text-brand-700'
                      : 'border-ink-200 bg-white text-ink-600 hover:border-brand-300 hover:bg-brand-50/40'
                  }`}
                >
                  <Comp className="size-4" strokeWidth={1.75} />
                </button>
              )
            })}
          </div>
        </div>
      )}

      {mode === 'upload' && (
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
            onChange={onFileChange}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-100 disabled:opacity-60"
          >
            {uploading ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
            {uploading ? 'Subiendo…' : 'Subir imagen'}
          </button>
          <p className="mt-1.5 text-[11px] text-ink-500">
            PNG, JPG, WebP, GIF o AVIF — máx 10 MB. Se guarda en el banco de imágenes.
          </p>
          {uploadError && (
            <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-700">
              {uploadError}
            </div>
          )}
        </div>
      )}

      {mode === 'url' && (
        <div>
          <div className="relative">
            <ImageIcon className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-ink-400" />
            <input
              type="url"
              value={value.startsWith('http') ? value : ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder="https://cdn.ejemplo.com/icono.svg"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-8 pr-3 font-mono text-xs outline-none focus:border-brand-500"
            />
          </div>
          <p className="mt-1.5 text-[11px] text-ink-500">
            URL absoluta (PNG, SVG, WebP). Útil para íconos hospedados en otro CDN.
          </p>
        </div>
      )}
    </div>
  )
}

function ModeTab({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${
        active
          ? 'bg-brand-600 text-white shadow-sm'
          : 'text-ink-600 hover:bg-ink-100'
      }`}
    >
      {icon}
      {children}
    </button>
  )
}

export default IconPicker
